import { Drawer, Button as AntdButton, Skeleton } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft2,
  ArrowRight2,
  Edit2,
  CloseCircle,
  TickCircle,
  DocumentDownload,
  RepeateMusic,
} from 'iconsax-react';
import Button from '../atoms/Button';
import BusinessCardInfo from './BusinessCardInfo';
import BusinessCardAttachment from './BusinessCardAttachments';
import EditModal from './EditModal';
import ReturnModal from './ReturnModal';
import RejectModal from './Reject';
import ApproveModal from './Approve';
import {
  useGetOperationActionsQuery,
  useGetSingleRequestQuery,
} from '../../store/services/business-requests';
import FlagComponent from 'react-world-flags';

// Type assertion to fix react-world-flags compatibility with React 18
const Flag = FlagComponent as React.ComponentType<{
  code: string;
  width?: string;
  height?: string;
}>;
import Helpers from '../../lib/helpers';
import TitleText from '../atoms/TitleText';
import dayjs from 'dayjs';
import ActivityLogsList from '../AdminLogs/IndividualLog/ActivityLogsList';

type Props = {
  requestId: string;
  requestIds?: string[];
  open: boolean;
  onClose: () => void;
  onRequestIdChange: (id: string) => void;
};

type FooterActionsProps = {
  requestId: string;
  setIsEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalApproveOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalRejectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalReturnOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

type DocumentType = {
  type: string;
  blobFileUrl?: string;
  name: string;
  profileId: string;
  blobSize: number;
};

type InfoItem = {
  text: string;
  value: React.ReactNode;
  code?: string;
  isEdited?: boolean;
};

type RequestData = {
  companyName?: string;
  companyEmail?: string;
  companyInfo?: {
    items: InfoItem[];
  };
  addressInfo?: {
    items: InfoItem[];
  };
  companyPapersInfo?: {
    items: InfoItem[];
  };
  commericalDocs?: DocumentType[];
  delegateDocs?: DocumentType[];
  nationalDocs?: DocumentType[];
  taxDocs?: DocumentType[];
  images?: DocumentType[];
};

const FooterActions = ({
  requestId,
  setIsEditOpen,
  setIsModalApproveOpen,
  setIsModalRejectOpen,
  setIsModalReturnOpen,
}: FooterActionsProps) => {
  const { t } = useTranslation();
  const { data } = useGetOperationActionsQuery(
    { id: requestId },
    { refetchOnMountOrArgChange: true, skip: !requestId }
  );
  const actions = data?.payload;
  const hasAnyAction =
    actions?.canApprove ||
    actions?.canEdit ||
    actions?.canReject ||
    actions?.canReturn;

  if (!hasAnyAction) return null;

  return (
    <div
      className="footer-actions flex gap-3 justify-between"
      data-test-id="request-details-footer"
    >
      <div className="flex gap-3">
        {actions?.canEdit && (
          <Button
            variant="info"
            size="sm"
            icon={<Edit2 size={16} color="#ffffff" variant="Bold" />}
            onClick={() => setIsEditOpen(true)}
            className="!rounded-lg"
            data-test="request-details-edit-btn"
          >
            {t('home.edit')}
          </Button>
        )}
        {actions?.canReturn && (
          <Button
            variant="warning"
            size="sm"
            icon={<RepeateMusic size={16} color="#ffffff" />}
            onClick={() => setIsModalReturnOpen(true)}
            className="!rounded-lg"
            data-test="request-details-return-btn"
          >
            {t('home.return')}
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        {actions?.canReject && (
          <Button
            variant="danger"
            size="sm"
            icon={<CloseCircle size={16} color="#ffffff" />}
            onClick={() => setIsModalRejectOpen(true)}
            className="!rounded-lg"
            data-test="request-details-reject-btn"
          >
            {t('home.reject')}
          </Button>
        )}
        {actions?.canApprove && (
          <Button
            variant="success"
            size="sm"
            icon={<TickCircle size={16} color="#ffffff" />}
            onClick={() => setIsModalApproveOpen?.(true)}
            className="!rounded-lg"
            data-test="request-details-approve-btn"
          >
            {t('home.approve')}
          </Button>
        )}
      </div>
    </div>
  );
};

type HeaderProps = {
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onClose?: () => void;
};

const Header = ({
  onPrevious,
  onNext,
  hasPrevious = true,
  hasNext = true,
  onClose,
}: HeaderProps) => {
  const { t } = useTranslation();
  return (
    <div
      className="header-title flex items-center justify-between gap-2 pt-4"
      data-test-id="request-details-header"
    >
      <div className="flex items-center gap-2">
        <TitleText
          text={t('home.applicationDetails')}
          className="!font-semibold !leading-6"
        />
        <AntdButton
          className="text-text-secondary !text-sm"
          color="default"
          variant="text"
          icon={<ArrowLeft2 size={12} />}
          onClick={onPrevious}
          disabled={!hasPrevious}
          data-test="request-details-prev-btn"
        >
          {t('home.prev')}
        </AntdButton>
        <AntdButton
          className="text-text-secondary !text-sm"
          color="default"
          variant="text"
          icon={<ArrowRight2 size={12} />}
          iconPosition="end"
          onClick={onNext}
          disabled={!hasNext}
          data-test="request-details-next-btn"
        >
          {t('home.next')}
        </AntdButton>
      </div>
      {onClose && (
        <AntdButton
          type="text"
          variant="text"
          color="default"
          onClick={onClose}
          className="!p-1.5 !min-w-0 !h-auto rounded"
          icon={
            <CloseCircle
              size={20}
              variant="Bold"
              className="text-light-text-primary"
            />
          }
          data-test="request-details-close-btn"
        />
      )}
    </div>
  );
};

const RequestDetails = ({
  requestId,
  requestIds = [],
  open,
  onClose,
  onRequestIdChange,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { data: singleRequestData, isLoading: isLoadingRequest } =
    useGetSingleRequestQuery(
      { requestId: requestId },
      { skip: !requestId, refetchOnMountOrArgChange: true }
    );

  const payload = singleRequestData?.payload;

  const data = useMemo((): RequestData => {
    if (!payload) return {};
    const {
      applicant,
      company,
      address,
      documents,
      status,
      editedFields = [],
    } = payload;
    const isEdited = (fieldKey: string) => editedFields.includes(fieldKey);
    const comDocs: DocumentType[] = (documents?.commercialRegister ?? []).map(
      (d) => ({
        type: d.type,
        blobFileUrl: d.blobFileUrl,
        name: d.name,
        profileId: d.profileId,
        blobSize: d.blobSize,
      })
    );
    const delegateDocs: DocumentType[] = (documents?.delegation ?? []).map(
      (d) => ({
        type: d.type,
        blobFileUrl: d.blobFileUrl,
        name: d.name,
        profileId: d.profileId,
        blobSize: d.blobSize,
      })
    );
    const nationalDocs: DocumentType[] = (documents?.nationalId ?? []).map(
      (d) => ({
        type: d.type,
        blobFileUrl: d.blobFileUrl,
        name: d.name,
        profileId: d.profileId,
        blobSize: d.blobSize,
      })
    );
    const taxDocs: DocumentType[] = (documents?.taxNumber ?? []).map((d) => ({
      type: d.type,
      blobFileUrl: d.blobFileUrl,
      name: d.name,
      profileId: d.profileId,
      blobSize: d.blobSize,
    }));
    const flagCode = Helpers.getCountryCodeFromCallingCode(
      applicant?.phoneCode ?? ''
    );

    return {
      companyName: company?.name,
      companyEmail: applicant?.email,
      companyInfo: {
        items: [
          {
            text: t('home.requestStatus'),
            value: status?.name,
            code: status?.code,
            isEdited: isEdited('status'),
          },
          {
            text: t('home.firstName'),
            value: applicant?.firstName || '-',
            isEdited: isEdited('firstName'),
          },
          {
            text: t('home.lastName'),
            value: applicant?.lastName || '-',
            isEdited: isEdited('lastName'),
          },
          {
            text: t('home.email'),
            value: applicant?.email || '-',
            isEdited: isEdited('email'),
          },
          {
            text: t('home.phoneNumber'),
            value: (
              <div className="flex gap-3">
                <Flag code={flagCode} width="20" height="20" />
                <p>
                  {applicant?.phoneCode ?? ''}
                  {applicant?.phoneNumber ?? '-'}
                </p>
              </div>
            ),
            isEdited: isEdited('phoneNumber'),
          },
          {
            text: t('home.dateOfBirth'),
            value: applicant?.dob
              ? dayjs(applicant?.dob).format('DD/MM/YYYY')
              : '-',
            isEdited: isEdited('dob'),
          },
          {
            text: t('home.username'),
            value: applicant?.userName || '-',
            isEdited: isEdited('username'),
          },
          {
            text: t('home.companyName'),
            value: company?.name || '-',
            isEdited: isEdited('companyName'),
          },
          {
            text: t('home.companyType'),
            value: company?.companyTypeName || '-',
            isEdited: isEdited('companyType'),
          },
        ],
      },
      addressInfo: {
        items: [
          {
            text: t('home.country'),
            value: address?.countryName || '-',
            isEdited: isEdited('address.country'),
          },
          {
            text: t('home.state'),
            value: address?.stateName || '-',
            isEdited: isEdited('address.state'),
          },
          {
            text: t('home.streetAddress'),
            value: address?.street || '-',
            isEdited: isEdited('address.street'),
          },
        ],
      },
      companyPapersInfo: {
        items: [
          {
            text: t('home.taxNumber'),
            value: company?.taxNumber || '-',
            isEdited: isEdited('taxNumber'),
          },
          {
            text: t('home.commercialRegister'),
            value: company?.commercialRegister || '-',
            isEdited: isEdited('commercialRegister'),
          },
          {
            text: t('home.applicantType'),
            value: applicant?.applicantType || '-',
            isEdited: isEdited('applicantType'),
          },
        ],
      },
      commericalDocs: comDocs,
      delegateDocs,
      nationalDocs,
      taxDocs,
      images: [],
    };
  }, [payload, t]);

  const currentIndex = requestIds.findIndex((id) => String(id) === requestId);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onRequestIdChange(requestIds[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < requestIds.length - 1) {
      onRequestIdChange(requestIds[currentIndex + 1]);
    }
  };

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < requestIds.length - 1;

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isModalApproveOpen, setIsModalApproveOpen] = useState(false);
  const [isModalRejectOpen, setIsModalRejectOpen] = useState(false);
  const [isModalReturnOpen, setIsModalReturnOpen] = useState(false);

  // Determine drawer placement based on language direction
  const placement = language === 'ar' ? 'left' : 'right';

  return (
    <Drawer
      onClose={onClose}
      placement={placement}
      open={open}
      closable={false}
      width={576}
      className="!overflow-hidden"
      footer={
        <FooterActions
          requestId={requestId}
          setIsEditOpen={setIsEditOpen}
          setIsModalRejectOpen={setIsModalRejectOpen}
          setIsModalApproveOpen={setIsModalApproveOpen}
          setIsModalReturnOpen={setIsModalReturnOpen}
        />
      }
      title={
        <Header
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onClose={onClose}
        />
      }
    >
      <div className="body">
        {isLoadingRequest ? (
          <div
            className="flex flex-col gap-6"
            data-test="request-details-loading"
          >
            <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 9 }} />
            <Skeleton active title={{ width: '35%' }} paragraph={{ rows: 4 }} />
            <Skeleton active title={{ width: '38%' }} paragraph={{ rows: 4 }} />
            <Skeleton active title={{ width: '45%' }} paragraph={{ rows: 2 }} />
            <Skeleton active title={{ width: '42%' }} paragraph={{ rows: 2 }} />
            <Skeleton active title={{ width: '48%' }} paragraph={{ rows: 2 }} />
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <Skeleton active title={{ width: '35%' }} />
              <div className="flex gap-3 flex-wrap mt-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton.Avatar
                    key={i}
                    active
                    shape="square"
                    className="!w-32 !h-32 !rounded-lg"
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <BusinessCardInfo
              title={t('home.companyInformation')}
              payload={data?.companyInfo}
              isStatus
            />

            <BusinessCardInfo
              title={t('home.addressInformation')}
              payload={data?.addressInfo}
            />

            <BusinessCardInfo
              title={t('home.companyPapers')}
              payload={data?.companyPapersInfo}
            />

            <BusinessCardAttachment
              title={t('home.commercialRegisterDocuments')}
              attachments={data?.commericalDocs}
            />

            <BusinessCardAttachment
              title={t('home.taxNumberDocuments')}
              attachments={data?.taxDocs}
            />

            <BusinessCardAttachment
              title={t('home.nationalDocuments')}
              attachments={data?.nationalDocs}
            />

            <BusinessCardAttachment
              title={t('home.delegationDocuments')}
              attachments={data?.delegateDocs}
            />

            {/* User Verification Section */}
            {data?.images && data.images.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-base">
                    {t('home.userVerification')}
                  </h3>
                  <button
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    onClick={() => {
                      data.images?.forEach((img) => {
                        if (img.blobFileUrl) {
                          window.open(img.blobFileUrl, '_blank');
                        }
                      });
                    }}
                  >
                    <DocumentDownload size={18} />
                    {t('home.downloadAll')}
                  </button>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {data.images.map((img, index) => (
                    <div
                      key={index}
                      className="w-32 h-32 rounded-lg overflow-hidden border border-gray-200"
                    >
                      <img
                        src={img.blobFileUrl}
                        alt={img.name || `User photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ActivityLogsList id={requestId} />
          </>
        )}
      </div>
      {isEditOpen && (
        <EditModal
          isEditOpen={isEditOpen}
          handleModalClose={(closedAfterSuccess) => {
            setIsEditOpen(false);
            if (closedAfterSuccess) onClose();
          }}
          requestId={requestId}
        />
      )}
      {isModalReturnOpen && (
        <ReturnModal
          isModalOpen={isModalReturnOpen}
          handleModalClose={(closedAfterSuccess) => {
            setIsModalReturnOpen(false);
            if (closedAfterSuccess) onClose();
          }}
          requestId={requestId}
        />
      )}
      {isModalApproveOpen && (
        <ApproveModal
          companyEmail={data?.companyEmail || ''}
          isModalOpen={isModalApproveOpen}
          handleModalClose={(closedAfterSuccess) => {
            setIsModalApproveOpen(false);
            if (closedAfterSuccess) onClose();
          }}
          requestId={requestId}
        />
      )}
      {isModalRejectOpen && (
        <RejectModal
          companyEmail={data?.companyEmail || ''}
          isModalOpen={isModalRejectOpen}
          handleModalClose={(closedAfterSuccess) => {
            setIsModalRejectOpen(false);
            if (closedAfterSuccess) onClose();
          }}
          requestId={requestId}
        />
      )}
    </Drawer>
  );
};

export default RequestDetails;
